import React, { ForwardedRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import Chart from '..';
import { ChartRef } from '../_config/_interface';
import { useChartYaxisRange } from '../_config/_hooks';
import TextBubbles from '@home/components/TextBubbles';
import { adjustChartMargin, setDataLabel, setXLabelAlign } from "@customComp/Chart/chartConfigs/plugs";
import { formatDisplayValue, formatTotalNum } from '../_config/_utils';
import { useStackBarOption } from '@customComp/Chart/chartConfigs/stackBarChartHooks';
import { addBarChartLabel } from '@/pages/dashboard/components/Chart/_config/plug';
import { DATALAB_TOTAL_KEY } from '../_config/_constant';
import { setToolTipSetting } from '@customComp/Chart/chartConfigs/tooltip';
import { changeLegendLocation } from "@customComp/Chart/chartConfigs/legend/legendUtils";
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { checkNullString } from '@/pages/datalab/utils/utils';
import { cloneDeep, orderBy } from 'lodash-es';
import { IStackBarChartProps } from '@customComp/Chart/chartConfigs/types';
import { setTooltipMaxSize } from '@customComp/Chart/chartConfigs/tooltip/config';

const StackBarChartInner: React.ForwardRefRenderFunction<ChartRef, IStackBarChartProps> = (props, ref: ForwardedRef<ChartRef>) => {
  const {
    chartData,
    metricNames,
    isOnlyOneDimension,
    chartConfig,
    fieldInfos,
    metricIsAllPercent,
    curMetricsName,
    metricInfo,
    incrementSum,
    setOtherConfigFunc,
    onEvents: externalOnEvents,
    tooltipMinTopPx,
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const chartRef = useRef<ChartRef>(null);
  const [ecInstance, setEcInstance] = useState(chartRef?.current?.instance());
  const [chartLayoutRect, setChartLayoutRect] = useState<{
    width: number;
    height: number;
    gridWidth: number;
    gridHeight: number;
  }>({ width: 0, height: 0, gridWidth: 0, gridHeight: 0 });
  const {
    yAxisRangeSwitch,
    xLabel,
    showDataLabel,
    labelContent,
    yAxisName,
  } = chartConfig || {};
  const hasYAxisName: boolean = Boolean(yAxisName);
  
  // 获取实例
  const instance = useCallback(
    () => chartRef?.current?.instance(),
    [chartRef],
  );
  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    ecInstance,
    instance
  }));
  
  // 创建浮窗formatter
  const tooltipFormatter = useMemo(() => {
    return (params: any[] | any): string => {
      try {
        const resolveDisplayConfig = (param: any): { accuracy: number; unit: string; } => {
          const defaultAccuracy = Number.isFinite(Number(metricInfo?.accuracy))
            ? Number(metricInfo?.accuracy)
            : 0;
          const defaultUnit = metricInfo?.unit ?? 'bmk';
          const display = param?.data?.display;
          if (!display || typeof display !== 'object') {
            return { accuracy: defaultAccuracy, unit: defaultUnit };
          }
          const directAccuracy = (display as { accuracy?: number; }).accuracy;
          const directUnit = (display as { unit?: string; }).unit;
          if (directAccuracy !== undefined || directUnit !== undefined) {
            return {
              accuracy: Number.isFinite(Number(directAccuracy))
                ? Number(directAccuracy)
                : defaultAccuracy,
              unit: directUnit ?? defaultUnit,
            };
          }
          const nestedDisplay = Object.values(display).find(item =>
            item && typeof item === 'object',
          ) as { accuracy?: number; unit?: string; } | undefined;
          return {
            accuracy: Number.isFinite(Number(nestedDisplay?.accuracy))
              ? Number(nestedDisplay?.accuracy)
              : defaultAccuracy,
            unit: nestedDisplay?.unit ?? defaultUnit,
          };
        };
        const formatTooltipValue = (param: any): string => {
          const { accuracy, unit } = resolveDisplayConfig(param);
          const value = param?.originalValue !== undefined ? param.originalValue : param?.value;
          const valueIsPercent = Boolean(param?.data?.isPercent || metricInfo?.column_date_type === 'percent');
          if (valueIsPercent) {
            return formatTotalNum(value, {
              ...metricInfo,
              accuracy,
              unit,
              column_date_type: 'percent',
            });
          }
          return formatDisplayValue({
            value,
            isPercent: false,
            accuracy,
            unit,
          });
        };
        const tooltipParams = Array.isArray(params) ? params : [params];
        // 过滤掉汇总系列的数据和无效数据
        const filteredParams = tooltipParams.filter((item: any) => {
          return item?.seriesName !== DATALAB_TOTAL_KEY && typeof item?.data === 'object';
        });
        
        if (filteredParams.length === 0) {
          return '';
        }
        
        // 获取X轴标签（从第一个参数获取）
        const axisValueLabel = filteredParams[0]?.axisValueLabel || filteredParams[0]?.name || '';
        
        // 单维度显示
        if (isOnlyOneDimension) {
          let htmlStr = `<div class='dbl-tooltip-formatter-analysis-chart'>`;
          htmlStr += `<div class='dbl-tooltip-chart-container'>`;
          htmlStr += `<div class='dbl-tooltip-chart-data-list'>`;
          htmlStr += `<div class='dbl-tooltip-chart-data-item'>`;
          htmlStr += `<p>${axisValueLabel}</p>`;
          htmlStr += `</div>`;
          
          filteredParams.forEach((param: any) => {
            if (param?.data) {
              const text = formatTooltipValue({
                ...param,
                originalValue: Array.isArray(param.value) ? param.value[0] : param.value,
              });
              htmlStr += `<div class='dbl-tooltip-chart-data-item'>`;
              htmlStr += `<p class='dbl-tooltip-chart-data-item-seriesName'>${param.marker || ''}${curMetricsName || ''}</p>`;
              htmlStr += `<p class='dbl-tooltip-chart-data-item-value'>${text}</p>`;
              htmlStr += `</div>`;
            }
          });
          
          htmlStr += `</div>`;
          htmlStr += `</div>`;
          htmlStr += `</div>`;
          return htmlStr;
        }
        
        // 多维度显示
        // 提取数值并排序
        const paramsWithValue = filteredParams.map((param: any) => {
          const value = Array.isArray(param.value) ? param.value[0] : param.value;
          return {
            ...param,
            numericValue: parseFloat(value || '0') || 0,
            originalValue: value,
          };
        });
        
        // 按数值从大到小排序
        const sortedParams = orderBy(
          paramsWithValue,
          [(item: any) => item.numericValue],
          ['desc']
        );
        
        // Others 系列排在最后
        sortedParams.sort((a: any, b: any) => {
          if (a?.seriesName === 'Others') return 1;
          if (b?.seriesName === 'Others') return -1;
          return 0;
        });
        
        // 计算占比和汇总值
        const total = sortedParams.reduce((sum: number, item: any) => {
          return sum + item.numericValue;
        }, 0);
        const totalDisplay = resolveDisplayConfig(sortedParams?.[0]);
        const percentAccuracy = Number.isFinite(Number(totalDisplay?.accuracy))
          ? Number(totalDisplay?.accuracy)
          : 2;
        const formatSharePercent = (value: number): string =>
          `${(value * 100).toFixed(percentAccuracy)}%`;
        
        // 计算每个系列的占比
        const paramsWithPercent = sortedParams.map((param: any) => {
          const value = param.numericValue;
          let percent = '';
          if (total !== 0 && value !== 0) {
            const percentValue = value / total;
            percent = formatSharePercent(percentValue);
          } else if (value === 0) {
            percent = formatSharePercent(0);
          } else {
            percent = '-';
          }
          return {
            ...param,
            percent,
          };
        });
        
        // 如果开启增量汇总，添加Total汇总行
        let finalParams = paramsWithPercent;
        if (incrementSum) {
          const isNull = total === 0 && sortedParams.every((item: any) => item.numericValue === 0);
          const formattedTotal = isNull ? '-' : formatTooltipValue({
            originalValue: total,
            data: {
              isPercent: metricInfo?.column_date_type === 'percent',
              display: totalDisplay,
            },
          });
          
          finalParams = [{
            marker: '',
            seriesName: isEn ? 'Total' : '总计',
            value: formattedTotal,
            percent: isNull ? '-' : formatSharePercent(1),
            data: {
              isTotal: true,
              axisPoint: axisValueLabel,
            },
            axisValueLabel,
            numericValue: total,
            originalValue: total,
          }, ...paramsWithPercent];
        }
        
        // 生成HTML
        let htmlStr = `<div class='dbl-tooltip-formatter-analysis-chart'>`;
        htmlStr += `<div class='dbl-tooltip-chart-container'>`;
        
        // 第一列：系列名称
        htmlStr += `<div class='dbl-tooltip-chart-data-list'>`;
        htmlStr += `<div class='dbl-tooltip-chart-data-item'>`;
        htmlStr += `<p style='height: 20px'>${finalParams[0]?.axisValueLabel || ''}</p>`;
        htmlStr += `</div>`;
        finalParams.forEach((param: any) => {
          const totalColClass = param?.data?.isTotal ? 'dbl-tooltip-chart-data-total' : '';
          htmlStr += `<div class='dbl-tooltip-chart-data-item'>`;
          htmlStr += `<p class='dbl-tooltip-chart-data-item-seriesName ${totalColClass}'>${param.marker || ''}${param.seriesName || ''}</p>`;
          htmlStr += `</div>`;
        });
        htmlStr += `</div>`;
        
        // 第二列：指标名称和数值
        htmlStr += `<div class='dbl-tooltip-chart-data-list'>`;
        htmlStr += `<div class='dbl-tooltip-chart-data-item'>`;
        htmlStr += `<p class='dbl-tooltip-chart-data-item-metricsName'>${curMetricsName || ''}</p>`;
        htmlStr += `</div>`;
        finalParams.forEach((param: any) => {
          const totalColClass = param?.data?.isTotal ? 'dbl-tooltip-chart-data-total' : '';
          const text = formatTooltipValue(param);
          htmlStr += `<div class='dbl-tooltip-chart-data-item'>`;
          htmlStr += `<p class='dbl-tooltip-chart-data-item-value ${totalColClass}'>${text}</p>`;
          htmlStr += `</div>`;
        });
        htmlStr += `</div>`;
        
        // 第三列：占比
        htmlStr += `<div class='dbl-tooltip-chart-data-list'>`;
        htmlStr += `<div class='dbl-tooltip-chart-data-item'>`;
        htmlStr += `<p class='dbl-tooltip-chart-data-item-shareName'>${isEn ? '% of Total' : '占比'}</p>`;
        htmlStr += `</div>`;
        finalParams.forEach((param: any) => {
          const totalColClass = param?.data?.isTotal ? 'dbl-tooltip-chart-data-total' : '';
          htmlStr += `<div class='dbl-tooltip-chart-data-item'>`;
          htmlStr += `<p class='dbl-tooltip-chart-data-item-percent ${totalColClass}'>${checkNullString(param?.percent) ? '-' : param?.percent || '-'}</p>`;
          htmlStr += `</div>`;
        });
        htmlStr += `</div>`;
        
        htmlStr += `</div>`;
        htmlStr += `</div>`;
        return htmlStr;
      } catch (error) {
        console.warn('[StackBarChart] Tooltip formatter error:', error);
        return '';
      }
    };
  }, [isOnlyOneDimension, incrementSum, metricInfo, curMetricsName, isEn]);
  
  let option = useStackBarOption({
    ...chartData,
    ecInstance,
    metricIsAllPercent,
    metricNames,
    isOnlyOneDimension,
    tooltipMinTopPx,
    fieldInfos,
    chartConfig,
    setOtherConfigFunc: (opt) => {
      let option = cloneDeep(opt);
      // 设置浮窗formatter
      if (option.tooltip && !Array.isArray(option.tooltip) && typeof option.tooltip === 'object') {
        option.tooltip.formatter = tooltipFormatter;
        option.tooltip.className = 'dbl-echarts-tooltip dbl-echarts-tooltip-padding';
      }
      // displayConfig 是 一个键值对象, key是指标名称, value是 {accuracy: 小数位数; unit: bmk或千分隔符}
      const displayConfig: Record<string, { accuracy: number; unit: string; }> = {};
      metricNames.forEach((metricName: string) => {
        const { accuracy, unit } = fieldInfos?.find((fieldInfo) => fieldInfo.name === metricName) || { accuracy: 0, unit: 'bmk' };
        displayConfig[metricName] = { accuracy, unit };
      });

      // 设置数据标签
      option = setDataLabel(option, chartConfig, {
        label: {
          position: isOnlyOneDimension ? 'top' : 'insideTop',
        },
        metricFormatter: (param, isPercent) => {
          const { accuracy = 0, unit = 'bmk' } = metricInfo || {};
          return formatDisplayValue({
            value: param.value,
            isPercent,
            accuracy,
            unit: unit || 'bmk',
          });
        },
      });

      // 设置图表边距
      adjustChartMargin(option, chartConfig, {
        ecInstance: instance?.(),
        rightAuto: chartConfig?.showDataLabel,
      });

      // 如果开启了汇总值标签
      if (showDataLabel && labelContent?.includes('total')) {
        option = addBarChartLabel(option, {
          stackName: DATALAB_TOTAL_KEY,
          totalFormatNum: (data: { value: string; }) => parseFloat(data?.value || '0') || 0,
          formatNum: (data: string) => {
            const { accuracy, unit, column_date_type } = metricInfo || { accuracy: 0, unit: 'bmk' };
            return formatDisplayValue({
              value: data,
              isPercent: column_date_type === 'percent',
              accuracy,
              unit,
            });
          },
          totalLabelFormat: (params: { dataIndex: string | number; }, { newFormattedTotalData }: any) => newFormattedTotalData?.[params?.dataIndex] || ""
        });
      }

      // 设置浮窗内容展示
      option = setToolTipSetting(option, chartConfig);
      // 设置tooltip最大尺寸
      option = setTooltipMaxSize(option, chartConfig, { chartLayoutRect });

      // 设置x轴label展示
      option = setXLabelAlign(option, { type: xLabel });

      // 设置图例位置
      changeLegendLocation({ option, position: chartConfig?.legendPosition });

      return setOtherConfigFunc ? setOtherConfigFunc?.(option) : option;
    },
  });
  
  
  option = setXLabelAlign(option, { type: chartConfig?.xLabel });
  
  // y轴range调整
  const rangeOption = useChartYaxisRange({ option, type: 'stack' });
  let newOption = option;
  if (yAxisRangeSwitch) {
    newOption = rangeOption;
  }
  
  const handleChartRendered = useCallback((): void => {
    const chartInstance = instance?.() as any;
    setEcInstance((prev) => {
      if (prev === chartInstance) {
        return prev;
      }
      return chartInstance;
    });
    const instanceRect = chartInstance?.getDom()?.getBoundingClientRect?.();
    const width = instanceRect?.width ?? 400;
    const height = instanceRect?.height ?? 379;
    const gridRect = chartInstance?.getModel?.()?.getComponent?.('grid', 0)
      ?.coordinateSystem?.getRect?.();
    const gridWidth = gridRect?.width ?? 400;
    const gridHeight = gridRect?.height ?? 379;
    setChartLayoutRect((prev) => {
      if (prev.width === width && prev.height === height) {
        return prev;
      }
      return { width, height, gridWidth, gridHeight };
    });
  }, [instance]);

  // 图例选择变化事件处理
  const onEvents = useMemo(() => {
    const events: Record<string, (event: any, chart: any) => void> = {
      rendered: handleChartRendered,
      finished: handleChartRendered,
      legendselectchanged: (event: any, chart: any) => {
        try {
          if (!chart) {
            return;
          }
          const option = chart.getOption();
          const eventSelected = event.selected;
          const optionSeries = option.series;
          
          // 计算选中的series的data的汇总值
          if (Array.isArray(optionSeries) && showDataLabel && labelContent?.includes('total')) {
            const newTotalData = Array(optionSeries?.[0]?.data?.length).fill(0) || [];
            optionSeries.slice(0, optionSeries?.length - 1).forEach((seriesItem: { name: string | number; data: any[]; }) => {
              if (Array.isArray(seriesItem?.data) && eventSelected[seriesItem?.name]) {
                seriesItem?.data?.forEach((data, index) => {
                  newTotalData[index] += parseFloat(data?.value || data || '0') || 0;
                });
              }
            });
            const newFormattedTotalData = newTotalData.map(data => formatTotalNum(data, metricInfo));
            // 确保最后一个 series 存在且 label 存在
            if (optionSeries[optionSeries?.length - 1]?.label) {
              optionSeries[optionSeries?.length - 1].label.formatter = (params: { dataIndex: number; }) => newFormattedTotalData[params.dataIndex] || '';
              chart.setOption(option);
            }
          }
        } catch (error) {
          console.warn('[StackBarChart] Legend select changed error:', error);
        }
      },
      ...(externalOnEvents ?? {}),
    };
    return events;
  }, [externalOnEvents, handleChartRendered, labelContent, metricInfo, showDataLabel]);
  
  return <div className="analysis-chart-container">
    <div
      className={classNames('dbl-charts-top-yAxis-text', {
        'dbl-charts-top-yAxis-text--hidden': !hasYAxisName,
      })}
    >
      <div className='dbh-text-exceeds-bubble-left'>
        <TextBubbles
          text={yAxisName}
        />
      </div>
    </div>
    <div className='analysis-chart-content'>
      <Chart ref={chartRef} option={newOption} notMerge={true} onEvents={onEvents} />
    </div>
  </div>;
};

const StackBarChart = React.forwardRef(StackBarChartInner);

export default StackBarChart;